/**
 * Donation Progress Service
 *
 * Loads donation progress on demand and applies server-pushed updates.
 * - Request dedup: concurrent `loadProgress` calls for the same event share one RPC.
 * - SSE updates land via `applyServerUpdate` (wired in live-event-state-service).
 */

import { inject } from "../../di";
import type { IDonationProgressService, IWritableDonationProgressState, DonationProgress } from "./types";
import type { IRpcService } from "../rpc/types";

interface ProgressRpcResult {
  targetCents: number;
  raisedCents: number;
  donorCount: number;
}

export class DonationProgressService implements IDonationProgressService {
  @inject<IWritableDonationProgressState>("writableDonationProgressState")
  private state!: IWritableDonationProgressState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  /** Pending RPC dedup map: liveEventId -> in-flight promise. */
  private pendingRequests = new Map<string, Promise<void>>();

  loadProgress(liveEventId: string | undefined): void {
    if (!liveEventId) return;
    if (this.state.progress.get().has(liveEventId)) return;
    if (this.pendingRequests.has(liveEventId)) return;

    const newLoading = new Set(this.state.loading.get());
    newLoading.add(liveEventId);
    this.state.loading.set(newLoading);

    const requestPromise = this.doLoadProgress(liveEventId);
    this.pendingRequests.set(liveEventId, requestPromise);
  }

  applyServerUpdate(update: DonationProgress): void {
    const next = new Map(this.state.progress.get());
    next.set(update.liveEventId, update);
    this.state.progress.set(next);
  }

  private async doLoadProgress(liveEventId: string): Promise<void> {
    try {
      const result = await this.rpcService.call<ProgressRpcResult>("get-donation-progress", {
        liveEventId,
      });
      this.applyServerUpdate({ liveEventId, ...result });
    } catch (error) {
      console.error("[DonationProgressService] Error loading donation progress:", error);
    } finally {
      const newLoading = new Set(this.state.loading.get());
      newLoading.delete(liveEventId);
      this.state.loading.set(newLoading);

      this.pendingRequests.delete(liveEventId);
    }
  }
}
